import React, { useEffect, useMemo, useState } from "react";
import { axiosSalary } from "../../api/axiosInstance";
import { FaFileExcel } from "react-icons/fa";
import { useAuth } from "../../auth/AuthContext";

const PeriodicReportByRest = () => {

  const { user } = useAuth();
  const groupId = user?.groupId;
  const restId = user?.restaurantId;

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState("");

  /* ================= FETCH ================= */
  const fetchReport = async () => {
    if (!fromDate || !toDate) return;

    try {
      setLoading(true);

      const res = await axiosSalary.get(
        `/DailyAttendanceReport/DailyReportByRestaurant?groupId=${groupId}&restId=${restId}&fromDate=${fromDate}&toDate=${toDate}`
      );

      setData(res.data || []);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= DEFAULT DATE ================= */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) fetchReport();
  }, [fromDate, toDate]);

  /* ================= DAYS AVAILABLE ================= */
  const totalDaysRange = useMemo(() => {
    if (!fromDate || !toDate) return 0;
    const start = new Date(fromDate);
    const end = new Date(toDate);
    return Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
  }, [fromDate, toDate]);

  /* ================= EMPLOYEE LIST ================= */
  const employeeList = useMemo(() => {
    const map = {};
    data.forEach(d => {
      map[d.empId] = d.empName;
    });
    return Object.entries(map).map(([id, name]) => ({
      empId: id,
      empName: name
    }));
  }, [data]);

  /* ================= FILTER ================= */
  const filteredData = useMemo(() => {
    let temp = data;

    if (selectedEmp) {
      temp = temp.filter(d => d.empId === Number(selectedEmp));
    }

    if (fromDate && toDate) {
      temp = temp.filter(d => {
        const rowDate = d.attendanceDate.split("T")[0];
        return rowDate >= fromDate && rowDate <= toDate;
      });
    }

    return temp;
  }, [data, selectedEmp, fromDate, toDate]);

  /* ================= SUMMARY ================= */
  const empSummary = useMemo(() => {
    if (!filteredData.length) return null;

    return {
      empId: filteredData[0].empId,
      empName: filteredData[0].empName,
      daysWorked: filteredData.length,
      present: filteredData.filter(d => d.isPresent).length,
      absent: filteredData.filter(d => d.isAbsent).length,
      late: filteredData.filter(d => d.isLate).length,
      workingMinutes: filteredData.reduce((a,b)=>a+(b.workingMinutes||0),0),
      overtime: filteredData.reduce((a,b)=>a+(b.overtimeMinutes||0),0),
    };
  }, [filteredData]);

  /* ================= EXPORT ================= */
 const exportExcel = () => {
  if (!empSummary || !filteredData.length) return;

  // ✅ SUMMARY ROW
  const summaryRow = [{
    EmployeeName: empSummary.empName,
    EmpId: empSummary.empId,
    FromDate: fromDate,
    ToDate: toDate,
    DaysAvailable: totalDaysRange,
    DaysWorked: empSummary.daysWorked,
    Present: empSummary.present,
    Absent: empSummary.absent,
    Late: empSummary.late,
    WorkingMinutes: empSummary.workingMinutes,
    Overtime: empSummary.overtime,
  }];

  // ✅ DAILY DATA
  const detailRows = filteredData.map(r => ({
    EmpId: r.empId,
    Name: r.empName,
    Date: r.attendanceDateDisplay,
    WorkingMinutes: r.workingMinutes,
    Status: r.isAbsent
      ? "Absent"
      : r.isLate
      ? "Late"
      : "Present"
  }));

  // ✅ CREATE CSV
  const csv =
    "data:text/csv;charset=utf-8," +
    [
      // Summary Header
      Object.keys(summaryRow[0]).join(","),
      Object.values(summaryRow[0]).join(","),

      "", // empty line

      // Detail Header
      Object.keys(detailRows[0]).join(","),
      ...detailRows.map(r => Object.values(r).join(",")),
    ].join("\n");

  const link = document.createElement("a");
  link.href = encodeURI(csv);
  link.download = `${empSummary.empName}_Full_Report.csv`;
  link.click();
};

  return (
    <div className="bg-white p-5 rounded-2xl shadow border">

      {/* HEADER */}
      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">

        <h2 className="text-xl font-semibold">Attendance Report</h2>

        <div className="flex gap-2 flex-wrap">

          <input type="date" value={fromDate}
            onChange={(e)=>setFromDate(e.target.value)}
            className="border px-2 py-1 rounded"/>

          <input type="date" value={toDate}
            onChange={(e)=>setToDate(e.target.value)}
            className="border px-2 py-1 rounded"/>

          <select
            value={selectedEmp}
            onChange={(e)=>setSelectedEmp(e.target.value)}
            className="border px-2 py-1 rounded"
          >
            <option value="">Select Employee</option>
            {employeeList.map(emp => (
              <option key={emp.empId} value={emp.empId}>
                {emp.empName}
              </option>
            ))}
          </select>

          <button
            onClick={exportExcel}
            className="bg-green-600 text-white px-3 py-1 rounded flex items-center gap-1"
          >
            <FaFileExcel /> Export
          </button>

        </div>
      </div>

      {/* SUMMARY */}
      {selectedEmp && empSummary && (
        <div className="bg-blue-50 p-4 rounded-xl mb-4">

          <h3 className="font-semibold text-lg mb-2">
            {empSummary.empName}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">

            <div className="bg-white p-2 rounded shadow">
              Days Available
              <div className="font-bold">{totalDaysRange}</div>
            </div>

            <div className="bg-green-100 p-2 rounded">
              Present
              <div className="font-bold">{empSummary.present}</div>
            </div>

            <div className="bg-red-100 p-2 rounded">
              Absent
              <div className="font-bold">{empSummary.absent}</div>
            </div>

            <div className="bg-yellow-100 p-2 rounded">
              Late
              <div className="font-bold">{empSummary.late}</div>
            </div>

            <div className="bg-blue-100 p-2 rounded">
              Working
              <div className="font-bold">{empSummary.workingMinutes}</div>
            </div>

          </div>

          <p className="text-xs mt-2 text-gray-600">
            {fromDate} → {toDate}
          </p>

        </div>
      )}

      {/* TABLE */}
      <div className="border rounded-lg overflow-auto max-h-[400px]">

        <table className="w-full text-xs">

          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="p-2 border">ID</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Date</th>
              <th className="p-2 border">Work</th>
              <th className="p-2 border">Status</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="text-center p-4">Loading...</td></tr>
            ) : filteredData.length === 0 ? (
              <tr><td colSpan="5" className="text-center p-4">No Data</td></tr>
            ) : (
              filteredData.map((row, i) => (
                <tr key={i} className="hover:bg-blue-50">
                  <td className="p-2 border text-center">{row.empId}</td>
                  <td className="p-2 border">{row.empName}</td>
                  <td className="p-2 border text-center">{row.attendanceDateDisplay}</td>
                  <td className="p-2 border text-center">{row.workingMinutes}</td>
                  <td className="p-2 border text-center">
                    {row.isAbsent ? "🔴 Absent" : row.isLate ? "🟡 Late" : "🟢 Present"}
                  </td>
                </tr>
              ))
            )}
          </tbody>

        </table>
      </div>

    </div>
  );
};

export default PeriodicReportByRest;